/* eslint-disable max-len */
import React, { useEffect, useMemo, useState } from 'react';
import { SummaryInspProps, Defect } from '../types';
import tickIconRed from '../../../assets/Icons/tick-icon-red.svg';

// Helper function to create chunks of 15 for the first table and 25 for subsequent tables
const createChunks = (array: Defect[], firstChunkSize: number, subsequentChunkSize: number): Defect[][] => {
  const result: Defect[][] = [];
  if (array.length > 0) {
    result.push(array.slice(0, firstChunkSize));
  }
  for (let i = firstChunkSize; i < array.length; i += subsequentChunkSize) {
    result.push(array.slice(i, i + subsequentChunkSize));
  }

  return result;
};

function SummaryInspectionTable({ summaryDetails, secondaryColor }: SummaryInspProps) {
  const [defects, setDefects] = useState(summaryDetails?.Defects || []);
  const [ppDefect, setPPDefect] = useState({} as Defect);
  const [isPP, setIsPP] = useState(false);

  useEffect(() => {
    if (summaryDetails?.SpotTest) {
      setDefects(summaryDetails?.Defects?.filter((item) => item?.IsCodePP === false));
      setPPDefect(summaryDetails?.Defects?.find((item) => item?.IsCodePP === true) || {} as Defect);
    }
  }, [summaryDetails]);

  useEffect(() => {
    if (ppDefect && Object.keys(ppDefect)?.length > 0) {
      setIsPP(true);
    }
  }, [ppDefect]);

  const chunks = useMemo(() => createChunks(defects, 22, 25), [defects]);

  return (
    <div className="w100 centerAlign d-block">
      {/* case nomeasurable defects */}
      {chunks?.length === 0 && (
      <table
        className="bordered-table w70 noTableBreak"
        style={{ margin: '0px auto', display: 'block' }}
      >
        {/* Header Section */}
        <thead>
          <tr>
            <td colSpan={4} className="tableGreenHeading textCenter boldText" style={{ backgroundColor: secondaryColor }}>
              {`${summaryDetails?.VesselName}: ${summaryDetails?.TubeCount} Tubes`}
            </td>
          </tr>
          <tr>
            <td colSpan={4} className="tableGreenHeading textCenter boldText" style={{ backgroundColor: secondaryColor }}>
              {`Tubes Tested: ${summaryDetails?.TubesTested}`}
            </td>
          </tr>
          <tr>
            <td className="textRight w48">Significant/Measurable Indications</td>
            <td className="textCenter w26">Tubes Marked</td>
            <td className="textCenter w26">Percent of Bundle</td>
            <td className="textCenter w26">Action Req.</td>
          </tr>
        </thead>
        {/* Body Section */}
        <tbody>
          <tr>
            <td className="textCenter w48">NO MEASURABLE DEFECTS</td>
            <td className="textCenter w26">{}</td>
            <td className="textCenter w26">{}</td>
            <td className="textCenter w26">{}</td>
          </tr>
          {/* Totals row only on the last chunk */}
          <tr>
            <td className="textRight w48 boldText fs16 bgGray">Total</td>
            <td className="textCenter w26 boldText fs16 bgGray">0</td>
            <td className="textCenter w26 boldText fs16 bgGray">0</td>
            <td className="textCenter w26 bgGray">{}</td>
          </tr>
        </tbody>
      </table>
      )}
      {/* case if defects are present */}
      {chunks?.length > 0 && chunks.map((defectChunk, chunkIndex) => (
        <>
          {(chunkIndex > 0 && chunkIndex < chunks.length) && (
            <>
              <div className="page-break" />
              <div className="h40" />
            </>
          )}
          <table
            className="bordered-table w70 noTableBreak"
            style={{ margin: '0px auto', display: 'block' }}
          >
            {/* Header Section */}
            <thead>
              <tr>
                <td colSpan={4} className="tableGreenHeading textCenter boldText" style={{ backgroundColor: secondaryColor }}>
                  {`${summaryDetails?.VesselName}: ${summaryDetails?.TubeCount} Tubes`}
                </td>
              </tr>
              <tr>
                <td colSpan={4} className="tableGreenHeading textCenter boldText" style={{ backgroundColor: secondaryColor }}>
                  {`Tubes Tested: ${summaryDetails?.TubesTested}`}
                </td>
              </tr>
              <tr>
                <td className="textRight w48">Significant/Measurable Indications</td>
                <td className="textCenter w26">Tubes Marked</td>
                <td className="textCenter w26">Percent of Bundle</td>
                <td className="textCenter w26">Action Req.</td>
              </tr>
            </thead>
            {/* Body Section */}
            <tbody>
              {defectChunk.map((details) => (
                <tr>
                  <td className="textRight w48" style={{ color: details.ActionRequired ? 'red' : 'inherit' }}>{details.Defect}</td>
                  <td className="textCenter w26">{details.DefectTypeCount}</td>
                  <td className="textCenter w26">{details.Percentage}</td>
                  <td className="textCenter w26">{details.ActionRequired && <img src={tickIconRed} alt="icon" width="18px" />}</td>
                </tr>
              ))}
              {/* Totals row only on the last chunk */}
              {chunkIndex === chunks.length - 1 && (
              <tr>
                <td className="textRight w48 boldText fs16 bgGray">Total</td>
                <td className="textCenter w26 boldText fs16 bgGray">{isPP ? (summaryDetails.TotalDefects - ppDefect.DefectTypeCount).toFixed(2) : summaryDetails?.TotalDefects}</td>
                <td className="textCenter w26 boldText fs16 bgGray">{isPP ? (summaryDetails.TotalPercentage - ppDefect.Percentage).toFixed(2) : summaryDetails?.TotalPercentage}</td>
                <td className="textCenter w26 bgGray">{}</td>
              </tr>
              )}
            </tbody>
          </table>
        </>
      ))}
      {isPP && (
      <div className="pp_note_container">
        <p>
          PREVIOUSLY PLUGGED TUBES =
          {' '}
          {ppDefect?.DefectTypeCount}
          {' '}
          (
          {ppDefect?.Percentage}
          % OF BUNDLE)
          {' '}
          <br />
          NOTE: Tubes noted as &quot;Previously Plugged&quot; are not included in the
          {' '}
          <br />
          number of tubes tested during this inspection.
        </p>
      </div>
      )}
    </div>
  );
}

export default SummaryInspectionTable;
